import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Rocket, BadgeCheck, Zap, Trash2, Plus, Inbox, MapPinned, ShieldCheck, Copy, AlertTriangle, Bell, BellOff, TrendingUp, Eye, MessageSquare, Heart as HeartIcon, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CompanionCard, { prettyCity } from "@/components/CompanionCard";
import { Reveal } from "@/components/Reveal";
import FileUpload from "@/components/FileUpload";
import { pushSupported, currentSubscription, subscribeToPush, unsubscribeFromPush } from "@/lib/push";
import { startPayFastCheckout } from "@/lib/payfast";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const flatTrigger = "rounded-none border-white/15 bg-transparent text-white focus:ring-velvet-gold";
const flatContent = "rounded-none border-white/15 bg-velvet-coal text-white max-h-72";
const inputCls = "w-full border-b border-white/15 bg-transparent py-2.5 text-sm text-white placeholder:text-white/30 focus:border-velvet-gold focus:outline-none";

/** Advertiser Insights — per-listing views/taps/messages/favourites + ROI hint. */
function InsightsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/me/analytics")
      .then((r) => setData(r.data))
      .catch(() => setData({ listings: [], totals: {} }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-16 text-center text-sm font-light text-white/40">Loading insights…</div>;
  if (!data || data.listings.length === 0) {
    return (
      <div className="border border-white/10 p-16 text-center" data-testid="insights-empty">
        <TrendingUp className="mx-auto mb-4 text-velvet-gold" size={28} />
        <p className="font-heading text-3xl font-light text-white/60">No insights yet</p>
        <p className="mt-3 text-sm font-light text-white/40">Create a listing to start tracking views and taps.</p>
      </div>
    );
  }

  const t = data.totals || {};
  const totalTiles = [
    { icon: Eye, label: "Views (lifetime)", value: t.views || 0, color: "text-velvet-gold" },
    { icon: MessageSquare, label: "Messages (7d)", value: t.messages_week || 0, color: "text-velvet-rose" },
    { icon: HeartIcon, label: "Favourites", value: t.favourites || 0, color: "text-velvet-rose" },
    { icon: Sparkles, label: "WhatsApp taps", value: t.whatsapp_taps || 0, color: "text-velvet-gold" },
  ];

  return (
    <div className="space-y-10" data-testid="insights-panel">
      <div>
        <p className="mb-4 text-xs uppercase tracking-luxe text-velvet-gold">This week &middot; overview</p>
        <div className="grid grid-cols-2 gap-px bg-white/5 md:grid-cols-4">
          {totalTiles.map((tile) => {
            const Icon = tile.icon;
            return (
              <div key={tile.label} className="bg-velvet-ink p-6" data-testid={`insight-total-${tile.label.split(" ")[0].toLowerCase()}`}>
                <Icon size={18} className={tile.color} />
                <p className="mt-4 font-heading text-4xl font-light tabular-nums text-white">{tile.value}</p>
                <p className="mt-1 text-[10px] uppercase tracking-luxe text-white/40">{tile.label}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <p className="mb-4 text-xs uppercase tracking-luxe text-velvet-gold">Per-listing breakdown</p>
        <div className="overflow-x-auto border border-white/10">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="border-b border-white/10 text-[10px] uppercase tracking-luxe text-white/40">
              <tr>
                <th className="px-4 py-3 text-left">Listing</th>
                <th className="px-4 py-3 text-right">Views</th>
                <th className="px-4 py-3 text-right">Msgs 7d</th>
                <th className="px-4 py-3 text-right">Favs</th>
                <th className="px-4 py-3 text-right">WA taps</th>
                <th className="px-4 py-3 text-right">Rating</th>
                <th className="px-4 py-3 text-right">Featured</th>
              </tr>
            </thead>
            <tbody>
              {data.listings.map((l) => (
                <tr key={l.slug} className="border-b border-white/5" data-testid={`insight-row-${l.slug}`}>
                  <td className="px-4 py-3">
                    <Link to={`/companion/${l.slug}`} className="flex items-center gap-3 text-white hover:text-velvet-rose">
                      {l.hero_image && <img src={l.hero_image} alt="" className="h-9 w-9 object-cover" />}
                      <span>{l.name}</span>
                      {l.verified && <BadgeCheck size={14} className="text-velvet-gold" />}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-white/80">{l.views}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-velvet-rose">{l.messages_week}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-white/80">{l.favourites}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-velvet-gold">{l.whatsapp_taps}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-white/80">
                    {l.rating > 0 ? `${l.rating} ★` : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {l.featured ? (
                      <span className="rounded-full bg-velvet-gold px-3 py-1 text-[10px] uppercase tracking-luxe text-black">On</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-luxe text-white/30">Off</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ROI hint */}
      {t.views > 0 && (
        <div className="border border-velvet-gold/30 bg-gradient-to-br from-velvet-gold/10 via-transparent to-transparent p-8" data-testid="insights-roi">
          <p className="text-xs uppercase tracking-luxe text-velvet-gold">Boost ROI</p>
          <p className="mt-4 max-w-2xl font-heading text-2xl font-light leading-relaxed text-white md:text-3xl">
            {data.listings.some((l) => l.featured)
              ? <>Your featured listings pulled <span className="text-velvet-gold">{t.views.toLocaleString()}</span> views and <span className="text-velvet-rose">{t.messages_week}</span> messages this week.</>
              : <>You&rsquo;ve had <span className="text-velvet-gold">{t.views.toLocaleString()}</span> views without a featured boost. Imagine what a top-of-page slot could do.</>}
          </p>
          {!data.listings.some((l) => l.featured) && (
            <Link
              to="/advertise"
              data-testid="insights-boost-cta"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-velvet-gold px-6 py-3 text-xs uppercase tracking-luxe text-black hover:bg-white"
            >
              Feature a listing &mdash; R899/mo
            </Link>
          )}
        </div>
      )}

      <p className="text-[10px] uppercase tracking-luxe text-white/30">
        {data.period}
      </p>
    </div>
  );
}


function TouringDialog({ listing, cities, onDone }) {
  const [open, setOpen] = useState(false);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ city: "", town: "", start_date: "", end_date: "", note: "" });
  const towns = cities.find((c) => c.slug === form.city)?.towns || [];

  const loadTours = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/listings/${listing.slug}/tours`);
      setTours(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) loadTours(); /* eslint-disable-next-line */ }, [open]);

  const add = async () => {
    if (!form.city || !form.start_date || !form.end_date) {
      toast.error("City and dates are required"); return;
    }
    if (form.end_date < form.start_date) {
      toast.error("End date must be on or after start date"); return;
    }
    try {
      await api.post(`/listings/${listing.slug}/tours`, form);
      toast.success("Tour added");
      setForm({ city: "", town: "", start_date: "", end_date: "", note: "" });
      loadTours();
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (tourId) => {
    try {
      await api.delete(`/listings/${listing.slug}/tours/${tourId}`);
      toast.success("Tour removed");
      loadTours();
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const upcomingCount = tours.filter((t) => (t.end_date || "") >= today).length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`tour-${listing.slug}`} className="flex items-center gap-1 rounded-full border border-white/15 px-3 py-2 text-[10px] uppercase tracking-luxe text-white/60 hover:border-velvet-gold">
          <MapPinned size={12} /> Tours{upcomingCount ? ` (${upcomingCount})` : ""}
        </button>
      </DialogTrigger>
      <DialogContent className="border border-white/10 bg-velvet-coal text-white sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light">Touring schedule — {listing.name}</DialogTitle>
        </DialogHeader>

        {/* Existing tours */}
        <div className="border-b border-white/10 pb-4">
          <p className="mb-3 text-[10px] uppercase tracking-luxe text-velvet-gold">Upcoming tours</p>
          {loading ? (
            <p className="text-xs font-light text-white/40">Loading…</p>
          ) : tours.length === 0 ? (
            <p className="text-xs font-light text-white/40">No tours scheduled yet.</p>
          ) : (
            <div className="space-y-2">
              {tours.map((t) => {
                const active = t.start_date <= today && t.end_date >= today;
                const past = t.end_date < today;
                const cityName = cities.find((c) => c.slug === t.city)?.name || t.city;
                return (
                  <div
                    key={t.id}
                    data-testid={`tour-row-${t.id}`}
                    className={`flex items-center justify-between border px-4 py-2.5 ${past ? "border-white/5 opacity-40" : active ? "border-[#25D366]/40" : "border-white/10"}`}
                  >
                    <div>
                      <p className="text-sm text-white">
                        {cityName}{t.town ? ` · ${t.town}` : ""}
                        {active && <span className="ml-2 text-[10px] uppercase tracking-luxe text-[#25D366]">Live now</span>}
                        {past && <span className="ml-2 text-[10px] uppercase tracking-luxe text-white/40">Past</span>}
                      </p>
                      <p className="mt-0.5 text-[10px] uppercase tracking-luxe text-white/50 tabular-nums">
                        {t.start_date} → {t.end_date}
                      </p>
                      {t.note && <p className="mt-1 text-xs font-light text-white/50">{t.note}</p>}
                    </div>
                    <button
                      onClick={() => remove(t.id)}
                      data-testid={`tour-remove-${t.id}`}
                      className="text-[10px] uppercase tracking-luxe text-velvet-rose hover:text-white"
                    >
                      Remove
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Add new */}
        <div className="pt-2">
          <p className="mb-3 text-[10px] uppercase tracking-luxe text-velvet-gold">Add a new tour</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="mb-1 block text-[10px] uppercase tracking-luxe text-white/40">Destination city</label>
              <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v, town: "" })}>
                <SelectTrigger className={flatTrigger} data-testid="tour-city"><SelectValue placeholder="Select city" /></SelectTrigger>
                <SelectContent className={flatContent}>{cities.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <label className="mb-1 block text-[10px] uppercase tracking-luxe text-white/40">Town / Area</label>
              <Select value={form.town || "none"} onValueChange={(v) => setForm({ ...form, town: v === "none" ? "" : v })}>
                <SelectTrigger className={flatTrigger}><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent className={flatContent}><SelectItem value="none">— Any —</SelectItem>{towns.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-luxe text-white/40">Start</label>
              <input type="date" min={today} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={inputCls} data-testid="tour-start" />
            </div>
            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-luxe text-white/40">End</label>
              <input type="date" min={form.start_date || today} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={inputCls} data-testid="tour-end" />
            </div>
            <textarea placeholder="Note (optional) — e.g. 'Incalls at Sandton hotel only'" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={`${inputCls} col-span-2`} rows={2} />
            <div className="col-span-2 flex justify-end pt-2">
              <button onClick={add} data-testid="tour-save" className="rounded-full bg-velvet-gold px-6 py-2.5 text-[10px] uppercase tracking-luxe text-black hover:bg-white">
                Add tour
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VerifyDialog({ listing, onDone }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState(listing.verification_code || null);
  const [selfie, setSelfie] = useState(null);
  const [busy, setBusy] = useState(false);

  const requestCode = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/listings/${listing.slug}/request-verification-code`);
      setCode(data.code);
      toast.success("Verification code generated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!selfie) { toast.error("Upload a selfie holding the code"); return; }
    // Selfie was uploaded to /uploads/photo. Need to re-upload via /listings/{slug}/upload-selfie so backend links it.
    // The simplest path: user selects file → we upload directly via that endpoint. Refactor: use raw file input here.
    toast.info("Please use the 'Choose file' button below");
  };

  const handleSelfieFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/listings/${listing.slug}/upload-selfie`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Selfie submitted — awaiting admin review");
      setOpen(false);
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(code || "");
    toast.success("Code copied");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`verify-${listing.slug}`} className="flex items-center gap-1 rounded-full border border-white/15 px-3 py-2 text-[10px] uppercase tracking-luxe text-white/60 hover:border-velvet-gold">
          <ShieldCheck size={12} /> Get verified
        </button>
      </DialogTrigger>
      <DialogContent className="border border-white/10 bg-velvet-coal text-white sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light">Verify {listing.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <ol className="ml-4 list-decimal space-y-2 text-sm text-white/70">
            <li>Generate a one-time code below.</li>
            <li>Write the code on paper (or your palm).</li>
            <li>Take a clear selfie holding the code and upload it.</li>
            <li>Our team reviews within 24 hours.</li>
          </ol>

          {!code ? (
            <button onClick={requestCode} disabled={busy} data-testid="verify-code-btn" className="w-full rounded-full bg-white py-3 text-[10px] uppercase tracking-luxe text-black hover:bg-velvet-rose hover:text-white disabled:opacity-50">
              {busy ? "Generating…" : "Generate my code"}
            </button>
          ) : (
            <div className="border border-velvet-gold/40 bg-velvet-gold/5 p-6 text-center">
              <p className="text-[10px] uppercase tracking-luxe text-velvet-gold">Your one-time code</p>
              <p className="mt-2 font-heading text-4xl tracking-widest text-white" data-testid="verify-code">{code}</p>
              <button onClick={copyCode} className="mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-luxe text-white/60 hover:text-velvet-gold">
                <Copy size={11} /> Copy
              </button>
            </div>
          )}

          {code && (
            <div>
              <label className="mb-2 block text-[10px] uppercase tracking-luxe text-white/40">Upload selfie holding the code</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleSelfieFile}
                disabled={busy}
                data-testid="verify-selfie-input"
                className="text-xs text-white/60 file:mr-4 file:border file:border-white/20 file:bg-transparent file:px-4 file:py-2 file:text-xs file:uppercase file:tracking-luxe file:text-white"
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditListingDialog({ listing, cities, cats, races, onDone }) {
  const [open, setOpen] = useState(false);
  const towns = cities.find((c) => c.slug === listing.city)?.towns || [];
  const [gallery, setGallery] = useState(
    (listing.gallery || []).filter((u) => typeof u === "string").map((u, i) => ({ file_id: `existing-${i}`, url: u }))
  );

  const saveGallery = async () => {
    try {
      // Fetch full listing then update with new gallery
      const payload = {
        name: listing.name, city: listing.city, town: listing.town || "",
        age: listing.age, category: listing.category, race: listing.race || "",
        height: listing.height || "", tagline: listing.tagline || "",
        bio: listing.bio || "",
        languages: listing.languages || [], interests: listing.interests || [],
        vibes: listing.vibes || [],
        contact: listing.contact || {},
        incall: listing.incall, outcall: listing.outcall,
        hero_image: listing.hero_image,
        gallery: gallery.map((g) => g.url),
      };
      await api.put(`/listings/${listing.slug}`, payload);
      toast.success("Gallery updated");
      setOpen(false);
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`edit-${listing.slug}`} className="rounded-full border border-white/15 px-3 py-2 text-[10px] uppercase tracking-luxe text-white/60 hover:border-white/40">
          Photos
        </button>
      </DialogTrigger>
      <DialogContent className="border border-white/10 bg-velvet-coal text-white sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light">Manage gallery — {listing.name}</DialogTitle>
        </DialogHeader>
        <div className="pt-2">
          <FileUpload kind="photo" value={gallery} onChange={setGallery} max={8} label="Add or remove photos" />
          <button onClick={saveGallery} data-testid="save-gallery" className="mt-6 w-full rounded-full bg-white py-3 text-[10px] uppercase tracking-luxe text-black hover:bg-velvet-rose hover:text-white">
            Save changes
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Account() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [favourites, setFavourites] = useState([]);
  const [listings, setListings] = useState([]);
  const [convs, setConvs] = useState([]);
  const [cities, setCities] = useState([]);
  const [cats, setCats] = useState([]);
  const [races, setRaces] = useState([]);

  useEffect(() => {
    if (user === false) navigate("/login");
  }, [user, navigate]);

  const load = useCallback(async () => {
    const [f, l, c] = await Promise.all([
      api.get("/me/favourites"),
      api.get("/me/listings"),
      api.get("/me/conversations"),
    ]);
    setFavourites(f.data);
    setListings(l.data);
    setConvs(c.data);
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);
  useEffect(() => {
    api.get("/locations").then((r) => setCities(r.data.flatMap((p) => p.cities))).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data.map((c) => c.name))).catch(() => {});
    api.get("/races").then((r) => setRaces(r.data)).catch(() => {});
  }, []);

  const boost = async (slug) => {
    try {
      await startPayFastCheckout({ package_id: "boost_week", listing_slug: slug });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleAvail = async (slug) => {
    const { data } = await api.post(`/listings/${slug}/toggle-available`);
    toast.success(data.available_now ? "Marked available now" : "Marked unavailable");
    load();
  };

  const remove = async (slug) => {
    if (!window.confirm("Delete this listing? This cannot be undone.")) return;
    await api.delete(`/listings/${slug}`);
    toast.success("Listing deleted");
    load();
  };

  const unread = convs.reduce((s, c) => s + (c.unread || 0), 0);
  const isAdvertiser = user && (user.account_type === "advertiser" || user.role === "admin");

  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  useEffect(() => {
    if (!isAdvertiser || !pushSupported()) return;
    currentSubscription().then((s) => setPushOn(!!s));
  }, [isAdvertiser]);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushOn) {
        await unsubscribeFromPush();
        setPushOn(false);
        toast.success("Push alerts turned off");
      } else {
        await subscribeToPush();
        setPushOn(true);
        toast.success("You'll be alerted when a new request lands in your city.");
      }
    } catch (err) {
      toast.error(err.message || formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPushBusy(false);
    }
  };

  const becomeAdvertiser = async () => {
    try {
      await api.post("/auth/account-type", { account_type: "advertiser" });
      toast.success("You're now an Advertiser — start listing below.");
      window.location.reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const [confirmDelete, setConfirmDelete] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);

  const deleteAccount = async () => {
    if (confirmDelete !== "DELETE") {
      toast.error("Type DELETE to confirm");
      return;
    }
    try {
      await api.delete("/auth/me");
      toast.success("Your account has been deleted");
      // Clear local state and redirect
      setTimeout(() => { window.location.href = "/"; }, 800);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!user) return null;

  return (
    <div className="pt-28 md:pt-36" data-testid="account-page">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <Reveal>
          <p className="mb-4 text-xs uppercase tracking-luxe text-velvet-gold">Your account</p>
          <h1 className="font-heading text-5xl font-semibold leading-none tracking-tight text-white md:text-7xl">
            Welcome, {user.name}
          </h1>
          <p className="mt-6 text-sm font-light text-white/50">{user.email}</p>
        </Reveal>

        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/inbox" data-testid="account-inbox-link" className="flex items-center gap-2 rounded-full border border-velvet-gold/40 bg-velvet-gold/5 px-5 py-2.5 text-[10px] uppercase tracking-luxe text-velvet-gold hover:bg-velvet-gold hover:text-black">
            <Inbox size={13} /> Inbox {unread > 0 && <span className="rounded-full bg-velvet-rose px-2 py-0.5 text-[9px] text-white">{unread}</span>}
          </Link>
          {isAdvertiser && pushSupported() && (
            <button
              type="button"
              onClick={togglePush}
              disabled={pushBusy}
              data-testid="push-toggle"
              className={`flex items-center gap-2 rounded-full border px-5 py-2.5 text-[10px] uppercase tracking-luxe transition-colors disabled:opacity-50 ${
                pushOn
                  ? "border-[#25D366] bg-[#25D366]/5 text-[#25D366] hover:bg-[#25D366]/10"
                  : "border-white/15 text-white/60 hover:border-velvet-gold hover:text-velvet-gold"
              }`}
            >
              {pushOn
                ? <><Bell size={13} /> Disable request alerts</>
                : <><BellOff size={13} /> Enable request alerts</>}
            </button>
          )}
        </div>

        <Tabs defaultValue={isAdvertiser ? "listings" : "favourites"} className="mt-10">
          <TabsList className="mb-10 rounded-none border-b border-white/10 bg-transparent p-0">
            {isAdvertiser && (
              <TabsTrigger value="listings" data-testid="tab-my-listings" className="rounded-none text-xs uppercase tracking-luxe data-[state=active]:bg-transparent data-[state=active]:text-velvet-rose">My Listings</TabsTrigger>
            )}
            {isAdvertiser && (
              <TabsTrigger value="insights" data-testid="tab-insights" className="rounded-none text-xs uppercase tracking-luxe data-[state=active]:bg-transparent data-[state=active]:text-velvet-gold">Insights</TabsTrigger>
            )}
            <TabsTrigger value="favourites" data-testid="tab-favourites" className="rounded-none text-xs uppercase tracking-luxe data-[state=active]:bg-transparent data-[state=active]:text-velvet-rose">Favourites</TabsTrigger>
            {!isAdvertiser && (
              <TabsTrigger value="become" data-testid="tab-become-advertiser" className="rounded-none text-xs uppercase tracking-luxe data-[state=active]:bg-transparent data-[state=active]:text-velvet-gold">Become an Advertiser</TabsTrigger>
            )}
          </TabsList>

          {isAdvertiser && (
          <TabsContent value="listings">
            <div className="mb-8 flex justify-end">
              <Link to="/advertise" data-testid="account-new-listing" className="flex items-center gap-2 rounded-full bg-white px-6 py-3 text-xs uppercase tracking-luxe text-black transition-colors hover:bg-velvet-rose hover:text-white">
                <Plus size={15} /> New listing
              </Link>
            </div>
            {listings.length === 0 ? (
              <div className="border border-white/10 p-16 text-center">
                <p className="font-heading text-3xl font-light text-white/50">You have no listings yet</p>
                <Link to="/advertise" className="mt-6 inline-block text-xs uppercase tracking-luxe text-velvet-gold">Create your first listing</Link>
              </div>
            ) : (
              <div className="space-y-4">
                {listings.map((l) => (
                  <div key={l.id} data-testid={`my-listing-${l.slug}`} className="flex flex-col gap-4 border border-white/10 p-5 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex items-center gap-4">
                      <img src={l.hero_image} alt={l.name} className="h-20 w-16 flex-shrink-0 object-cover" />
                      <div>
                        <p className="flex items-center gap-2 text-white">
                          {l.name}
                          {l.verified && <BadgeCheck size={14} className="text-velvet-gold" title="Verified" />}
                          {l.featured && <span className="bg-velvet-gold px-2 py-0.5 text-[9px] uppercase tracking-luxe text-black">Featured</span>}
                        </p>
                        <p className="text-xs uppercase tracking-luxe text-white/40">{prettyCity(l.city)} · {l.category}</p>
                        {(() => {
                          const today = new Date().toISOString().slice(0, 10);
                          const upcoming = (l.tours || []).filter((t) => (t.end_date || "") >= today);
                          if (upcoming.length === 0 && l.tour?.city) {
                            // legacy fallback
                            return (
                              <p className="mt-1 text-[10px] uppercase tracking-luxe text-velvet-rose">
                                Touring {prettyCity(l.tour.city)} · {l.tour.start_date} – {l.tour.end_date}
                              </p>
                            );
                          }
                          if (upcoming.length === 0) return null;
                          const next = upcoming[0];
                          return (
                            <p className="mt-1 text-[10px] uppercase tracking-luxe text-velvet-rose">
                              {upcoming.length === 1 ? "Touring" : `${upcoming.length} tours · next`} {prettyCity(next.city)} · {next.start_date} – {next.end_date}
                            </p>
                          );
                        })()}
                        {l.verification_status === "pending" && (
                          <p className="mt-1 text-[10px] uppercase tracking-luxe text-velvet-gold">Verification pending review</p>
                        )}
                        {l.verification_status === "rejected" && (
                          <p className="mt-1 text-[10px] uppercase tracking-luxe text-velvet-rose">Verification rejected — try again</p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button onClick={() => toggleAvail(l.slug)} data-testid={`toggle-avail-${l.slug}`} className={`flex items-center gap-1 rounded-full border px-3 py-2 text-[10px] uppercase tracking-luxe ${l.available_now ? "border-[#25D366] text-[#25D366]" : "border-white/15 text-white/60"}`}>
                        <Zap size={12} /> {l.available_now ? "Available" : "Set available"}
                      </button>
                      <TouringDialog listing={l} cities={cities} onDone={load} />
                      <EditListingDialog listing={l} cities={cities} cats={cats} races={races} onDone={load} />
                      {!l.verified && l.verification_status !== "pending" && (
                        <VerifyDialog listing={l} onDone={load} />
                      )}
                      <button onClick={() => boost(l.slug)} data-testid={`boost-${l.slug}`} className="flex items-center gap-1 rounded-full bg-velvet-rose px-4 py-2 text-[10px] uppercase tracking-luxe text-black hover:bg-velvet-gold">
                        <Rocket size={12} /> Boost
                      </button>
                      <Link to={`/companion/${l.slug}`} className="rounded-full border border-white/15 px-3 py-2 text-[10px] uppercase tracking-luxe text-white/60 hover:border-white/40">View</Link>
                      <button onClick={() => remove(l.slug)} data-testid={`del-listing-${l.slug}`} className="text-white/40 hover:text-velvet-rose">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
          )}

          {isAdvertiser && (
          <TabsContent value="insights">
            <InsightsPanel />
          </TabsContent>
          )}


          <TabsContent value="favourites">
            {favourites.length === 0 ? (
              <div className="border border-white/10 p-16 text-center">
                <p className="font-heading text-3xl font-light text-white/50">No favourites yet</p>
                <Link to="/directory" className="mt-6 inline-block text-xs uppercase tracking-luxe text-velvet-gold">Browse the directory</Link>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
                {favourites.map((c, i) => (<CompanionCard key={c.id} companion={c} index={i} />))}
              </div>
            )}
          </TabsContent>

          {!isAdvertiser && (
            <TabsContent value="become">
              <div className="border border-velvet-gold/40 bg-velvet-gold/5 p-10" data-testid="become-advertiser-card">
                <p className="text-xs uppercase tracking-luxe text-velvet-gold">Advertiser access</p>
                <h3 className="mt-3 font-heading text-3xl font-light leading-tight text-white md:text-4xl">
                  Only advertisers can place ads.
                </h3>
                <p className="mt-4 max-w-2xl text-sm font-light text-white/60">
                  Your account is a Member account — you can browse, favourite, message, and review.
                  To publish a listing, switch to an Advertiser account. Free, instant, reversible.
                </p>
                <ul className="mt-6 space-y-2 text-sm font-light text-white/70">
                  <li>· Unlimited self-service listings</li>
                  <li>· Verification badge with selfie code</li>
                  <li>· Boost & feature packages</li>
                  <li>· Tour scheduling across cities</li>
                  <li>· Two-way inbox with members</li>
                </ul>
                <button
                  onClick={becomeAdvertiser}
                  data-testid="account-become-advertiser-btn"
                  className="mt-8 rounded-full bg-white px-8 py-3.5 text-xs uppercase tracking-luxe text-black transition-colors hover:bg-velvet-rose hover:text-white"
                >
                  Become an advertiser
                </button>
              </div>
            </TabsContent>
          )}
        </Tabs>

        {user.role !== "admin" && (
          <div className="mt-24 border border-velvet-rose/30 bg-velvet-rose/5 p-8" data-testid="danger-zone">
            <div className="flex items-start gap-4">
              <AlertTriangle className="mt-1 text-velvet-rose" size={20} />
              <div className="flex-1">
                <p className="text-xs uppercase tracking-luxe text-velvet-rose">Danger zone</p>
                <h3 className="mt-2 font-heading text-2xl font-light text-white">Delete your account</h3>
                <p className="mt-3 max-w-xl text-sm font-light text-white/60">
                  This permanently removes your profile, listings, favourites, reviews, conversations and forum posts.
                  This action cannot be undone.
                </p>
                <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                  <DialogTrigger asChild>
                    <button data-testid="delete-account-btn" className="mt-6 rounded-full border border-velvet-rose bg-transparent px-6 py-3 text-xs uppercase tracking-luxe text-velvet-rose transition-colors hover:bg-velvet-rose hover:text-white">
                      Delete my account
                    </button>
                  </DialogTrigger>
                  <DialogContent className="border border-velvet-rose/40 bg-velvet-coal text-white sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="font-heading text-2xl font-light">Delete your account?</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-2 text-sm font-light text-white/70">
                      <p>Everything tied to <b className="text-white">{user.email}</b> will be permanently removed:</p>
                      <ul className="ml-5 list-disc space-y-1 text-white/60">
                        <li>Your profile and login</li>
                        <li>All listings you own</li>
                        <li>Favourites, reviews, conversations</li>
                        <li>Forum threads and posts you authored</li>
                      </ul>
                      <p className="text-velvet-rose">Type <b>DELETE</b> below to confirm.</p>
                      <input
                        value={confirmDelete}
                        onChange={(e) => setConfirmDelete(e.target.value)}
                        placeholder="DELETE"
                        data-testid="delete-confirm-input"
                        className="w-full border border-white/15 bg-transparent px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-velvet-rose focus:outline-none"
                      />
                      <div className="flex gap-3 pt-2">
                        <button
                          onClick={() => { setDeleteOpen(false); setConfirmDelete(""); }}
                          className="flex-1 rounded-full border border-white/15 py-3 text-[10px] uppercase tracking-luxe text-white/70 hover:border-white/40"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={deleteAccount}
                          disabled={confirmDelete !== "DELETE"}
                          data-testid="delete-account-confirm"
                          className="flex-1 rounded-full bg-velvet-rose py-3 text-[10px] uppercase tracking-luxe text-white hover:bg-velvet-rose/80 disabled:opacity-40"
                        >
                          Permanently delete
                        </button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
